import { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CalendarX2,
  CircleAlert,
  FileX2,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  CampaignDto,
  CampaignImportBatchActionDto,
  CampaignImportBatchDto,
  CancelCampaignImportBatchResponseDtoOutput,
  ConfirmCampaignImportBatchResponseDtoOutput,
} from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import { formatShortDateTime } from '@/lib/utils';

export type BatchOutcome =
  | { kind: 'confirm'; result: ConfirmCampaignImportBatchResponseDtoOutput }
  | { kind: 'cancel'; result: CancelCampaignImportBatchResponseDtoOutput }
  | { kind: 'error' };

export interface BatchDetailPresenterProps {
  batch: CampaignImportBatchDto;
  campaigns: CampaignDto[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  isActing: boolean;
  outcome: BatchOutcome | null;
  onDismissOutcome: () => void;
  /** `undefined` means the whole batch; the list is never empty. */
  onConfirm: (campaignIds: CampaignImportBatchActionDto['campaignIds']) => void;
  onCancel: (campaignIds: CampaignImportBatchActionDto['campaignIds']) => void;
  onOpenCampaign: (campaignId: string) => void;
  onBack: () => void;
  onImportAnother: () => void;
}

type PendingDialog = 'confirm' | 'cancel' | null;

const STATUS_VARIANT = {
  draft: 'outline',
  scheduled: 'default',
  sent: 'secondary',
} as const;

function CountTile({
  slot,
  label,
  value,
  accent,
}: {
  slot: string;
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-lg border bg-card px-5 py-4">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${accent}`} />
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd
        data-testid={`count-${slot}`}
        className="mt-1 text-3xl font-semibold tabular-nums"
      >
        {value}
      </dd>
    </div>
  );
}

function OutcomePanel({
  outcome,
  onDismiss,
}: {
  outcome: BatchOutcome;
  onDismiss: () => void;
}) {
  const { LL } = useI18nContext();

  return (
    <div
      data-testid="batch-outcome"
      role="status"
      aria-live="polite"
      className="relative rounded-lg border bg-card p-5 pr-12"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8"
        onClick={onDismiss}
        aria-label={LL.campaignImport.batch.result.dismiss()}
      >
        <X aria-hidden />
      </Button>
      {outcome.kind === 'error' ? (
        <p className="flex items-center gap-2 text-sm font-medium text-destructive">
          <CircleAlert className="h-4 w-4 shrink-0" aria-hidden />
          {LL.campaignImport.batch.result.actionFailed()}
        </p>
      ) : null}
      {outcome.kind === 'confirm' ? (
        <div className="space-y-3">
          <p className="font-semibold">
            {LL.campaignImport.batch.result.confirmTitle()}
          </p>
          <p className="text-sm">
            {LL.campaignImport.batch.result.confirmSummary({
              count: outcome.result.confirmedCount,
            })}
            {outcome.result.ignoredCount > 0
              ? ` ${LL.campaignImport.batch.result.ignoredSummary({ count: outcome.result.ignoredCount })}`
              : ''}
          </p>
          {outcome.result.skippedInPast.length > 0 ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium">
                {LL.campaignImport.batch.result.skippedTitle()}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {outcome.result.skippedInPast.map((skipped) => (
                  <li key={skipped.id}>{skipped.name}</li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">
                {LL.campaignImport.batch.result.skippedHint()}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
      {outcome.kind === 'cancel' ? (
        <div className="space-y-1">
          <p className="font-semibold">
            {LL.campaignImport.batch.result.cancelTitle()}
          </p>
          <p className="text-sm">
            {LL.campaignImport.batch.result.cancelSummary({
              count: outcome.result.deletedCount,
            })}
            {outcome.result.preservedSentCount > 0
              ? ` ${LL.campaignImport.batch.result.preservedSummary({ count: outcome.result.preservedSentCount })}`
              : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export function BatchDetailPresenter({
  batch,
  campaigns,
  total,
  page,
  pageSize,
  onPageChange,
  selectedIds,
  onSelectedIdsChange,
  isActing,
  outcome,
  onDismissOutcome,
  onConfirm,
  onCancel,
  onOpenCampaign,
  onBack,
  onImportAnother,
}: BatchDetailPresenterProps) {
  const { LL, locale } = useI18nContext();
  const statusLabels = {
    draft: LL.campaignImport.batch.status.draft(),
    scheduled: LL.campaignImport.batch.status.scheduled(),
    sent: LL.campaignImport.batch.status.sent(),
  } as const;
  const [dialog, setDialog] = useState<PendingDialog>(null);

  const { draft, scheduled, sent } = batch.counts;
  const isEmpty = draft + scheduled + sent === 0 && total === 0;
  const hasSelection = selectedIds.length > 0;
  const selected = new Set(selectedIds);
  const selectedCampaigns = campaigns.filter((c) => selected.has(c.id));

  // What each action would touch, so the dialog can say it before anything happens.
  const toConfirm = hasSelection
    ? selectedCampaigns.filter((c) => c.status === 'draft').length
    : draft;
  const toDelete = hasSelection
    ? selectedCampaigns.filter((c) => c.status !== 'sent').length
    : draft + scheduled;
  const preserved = hasSelection
    ? selectedCampaigns.filter((c) => c.status === 'sent').length
    : sent;

  const allOnPageSelected =
    campaigns.length > 0 && campaigns.every((c) => selected.has(c.id));
  const someOnPageSelected = campaigns.some((c) => selected.has(c.id));
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const toggleRow = (id: string, checked: boolean) =>
    onSelectedIdsChange(
      checked ? [...selectedIds, id] : selectedIds.filter((s) => s !== id)
    );

  const runDialog = () => {
    const ids = hasSelection ? selectedIds : undefined;
    if (dialog === 'confirm') onConfirm(ids);
    if (dialog === 'cancel') onCancel(ids);
    setDialog(null);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-3"
          onClick={onBack}
        >
          <ArrowLeft aria-hidden />
          {LL.campaignImport.batch.back()}
        </Button>
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
          {LL.campaignImport.batch.pageTitle()}
        </h1>
        <p className="text-sm text-muted-foreground">
          {LL.campaignImport.batch.fileLabel({ fileName: batch.fileName })}
          {' · '}
          {LL.campaignImport.batch.importedAt({
            date: formatShortDateTime(batch.createdAt, locale),
          })}
        </p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-3">
        <CountTile
          slot="draft"
          label={LL.campaignImport.batch.counts.draft()}
          value={draft}
          accent="bg-amber-500"
        />
        <CountTile
          slot="scheduled"
          label={LL.campaignImport.batch.counts.scheduled()}
          value={scheduled}
          accent="bg-[#155DFC]"
        />
        <CountTile
          slot="sent"
          label={LL.campaignImport.batch.counts.sent()}
          value={sent}
          accent="bg-emerald-500"
        />
      </dl>
      <p className="sr-only" role="status" aria-live="polite">
        {LL.campaignImport.batch.counts.summaryAnnouncement({
          draft,
          scheduled,
          sent,
        })}
      </p>

      {outcome ? (
        <OutcomePanel outcome={outcome} onDismiss={onDismissOutcome} />
      ) : null}

      {isEmpty ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-14 text-center">
          <FileX2 className="h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="text-lg font-semibold">
            {LL.campaignImport.batch.empty.title()}
          </h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            {LL.campaignImport.batch.empty.description()}
          </p>
          <Button type="button" onClick={onImportAnother}>
            {LL.campaignImport.batch.empty.importAnother()}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium">
              {hasSelection
                ? LL.campaignImport.batch.actions.selected({
                    count: selectedIds.length,
                  })
                : ''}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={isActing || toConfirm === 0}
                onClick={() => setDialog('confirm')}
              >
                <CalendarCheck aria-hidden />
                {isActing
                  ? LL.campaignImport.batch.actions.working()
                  : hasSelection
                    ? LL.campaignImport.batch.actions.confirmSelected()
                    : LL.campaignImport.batch.actions.confirmBatch()}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isActing}
                onClick={() => setDialog('cancel')}
              >
                <CalendarX2 aria-hidden />
                {hasSelection
                  ? LL.campaignImport.batch.actions.cancelSelected()
                  : LL.campaignImport.batch.actions.cancelBatch()}
              </Button>
            </div>
          </div>
          {!hasSelection && draft === 0 ? (
            <p className="text-sm text-muted-foreground">
              {LL.campaignImport.batch.actions.noDraftToConfirm()}
            </p>
          ) : null}

          <div className="rounded-lg border">
            <Table aria-label={LL.campaignImport.batch.table.label()}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        allOnPageSelected
                          ? true
                          : someOnPageSelected
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={(value) =>
                        onSelectedIdsChange(
                          value === true ? campaigns.map((c) => c.id) : []
                        )
                      }
                      aria-label={LL.campaignImport.batch.table.selectAll()}
                    />
                  </TableHead>
                  <TableHead>{LL.campaignImport.batch.table.name()}</TableHead>
                  <TableHead>
                    {LL.campaignImport.batch.table.launchDate()}
                  </TableHead>
                  <TableHead>
                    {LL.campaignImport.batch.table.status()}
                  </TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    data-state={
                      selected.has(campaign.id) ? 'selected' : undefined
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(campaign.id)}
                        onCheckedChange={(value) =>
                          toggleRow(campaign.id, value === true)
                        }
                        aria-label={LL.campaignImport.batch.table.selectRow({
                          name: campaign.name,
                        })}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {campaign.name}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatShortDateTime(campaign.launchDateTime, locale)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[campaign.status]}>
                        {statusLabels[campaign.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onOpenCampaign(campaign.id)}
                        aria-label={LL.campaignImport.batch.table.open({
                          name: campaign.name,
                        })}
                      >
                        <ArrowRight aria-hidden />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pages > 1 ? (
            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-muted-foreground">
                {LL.campaignImport.batch.pagination.pageOf({ page, pages })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                {LL.campaignImport.batch.pagination.previous()}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => onPageChange(page + 1)}
              >
                {LL.campaignImport.batch.pagination.next()}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <AlertDialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
      >
        <AlertDialogContent>
          {dialog === 'confirm' ? (
            <AlertDialogHeader>
              <AlertDialogTitle>
                {LL.campaignImport.batch.confirmDialog.title()}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {hasSelection
                  ? LL.campaignImport.batch.confirmDialog.selectionDescription({
                      count: toConfirm,
                    })
                  : LL.campaignImport.batch.confirmDialog.batchDescription({
                      count: toConfirm,
                    })}{' '}
                {LL.campaignImport.batch.confirmDialog.pastNotice()}
              </AlertDialogDescription>
            </AlertDialogHeader>
          ) : null}
          {dialog === 'cancel' ? (
            <AlertDialogHeader>
              <AlertDialogTitle>
                {LL.campaignImport.batch.cancelDialog.title()}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {toDelete > 0
                  ? LL.campaignImport.batch.cancelDialog.description({
                      deleted: toDelete,
                    })
                  : LL.campaignImport.batch.cancelDialog.nothingToDelete()}
                {preserved > 0
                  ? ` ${LL.campaignImport.batch.cancelDialog.preserved({ preserved })}`
                  : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>
              {LL.campaignImport.batch.actions.goBack()}
            </AlertDialogCancel>
            {dialog === 'confirm' ? (
              <AlertDialogAction onClick={runDialog}>
                {LL.campaignImport.batch.confirmDialog.confirm()}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={runDialog}
                disabled={toDelete === 0}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {LL.campaignImport.batch.cancelDialog.confirm()}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
